import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, FileText } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileInput, checkFile } from "@/components/shared/FileInput";
import { PolicyFields, blankPolicy, policyValid } from "./PolicyDialog";

export const BulkUploadDialog = ({ open, onOpenChange, categories, people, onSaved, onCategoryAdded }) => {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { if (open) { setItems([]); setIdx(0); setResult(null); } }, [open]);
  const pick = (e) => {
    const files = [...(e.target.files || [])].filter((f) => checkFile(f));
    setItems(files.map((file) => ({ ...blankPolicy(), file })));
    setIdx(0);
  };
  const set = (k) => (v) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [k]: v?.target ? v.target.value : v } : it)));
  const cur = items[idx];
  const allValid = items.length > 0 && items.every(policyValid);
  const save = async () => {
    setBusy(true);
    const out = [];
    for (const it of items) {
      const fd = new FormData();
      Object.entries({ ...it, file: undefined }).forEach(([k, v]) => v !== undefined && fd.append(k, v ?? ""));
      fd.append("file", it.file);
      try { await api.post("/policies", fd, { headers: { "Content-Type": "multipart/form-data" } }); out.push({ name: it.file.name, ok: true }); } catch (err) { out.push({ name: it.file.name, ok: false, error: errMsg(err) }); }
    }
    setResult(out); setBusy(false); onSaved?.();
    if (out.every((o) => o.ok)) toast.success(`${out.length} polic${out.length === 1 ? "y" : "ies"} saved`);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bulk-policy-dialog">
        <DialogHeader><DialogTitle>Upload several policies</DialogTitle><DialogDescription>Pick the files, then enter each policy's details on the review screen. Nothing is guessed from file names.</DialogDescription></DialogHeader>
        {result ? (
          <div className="space-y-2" data-testid="bulk-result">
            {result.map((r, i) => <div key={i} className={`text-sm flex gap-2 ${r.ok ? "text-emerald-700" : "text-rose-700"}`}><FileText className="h-4 w-4 shrink-0" /> {r.name} — {r.ok ? "saved" : r.error}</div>)}
            <div className="flex justify-end pt-2"><Button onClick={() => onOpenChange(false)} data-testid="bulk-close">Close</Button></div>
          </div>
        ) : items.length === 0 ? (
          <div className="py-4"><FileInput testid="bulk-files" multiple onFile={() => {}} onChange={pick} /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-1" data-testid="bulk-file-list">
              {items.map((it, i) => <button key={i} type="button" onClick={() => setIdx(i)} data-testid={`bulk-file-${i}`} className={`text-xs px-2 py-1 rounded border max-w-[220px] truncate ${i === idx ? "bg-teal-600 text-white border-teal-600" : policyValid(it) ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-white border-slate-200 text-slate-600"}`} title={it.file.name}>{i + 1}. {it.file.name}</button>)}
            </div>
            <div className="text-xs text-slate-500">File {idx + 1} of {items.length}: <span className="font-medium text-slate-700 break-all">{cur.file.name}</span></div>
            <PolicyFields f={cur} set={set} categories={categories} people={people} onCategoryAdded={onCategoryAdded} testPrefix={`bulk-${idx}`} showFile={false} />
            <div className="flex items-center justify-between pt-1">
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" disabled={idx === 0} onClick={() => setIdx(idx - 1)} data-testid="bulk-prev"><ArrowLeft className="h-3.5 w-3.5 mr-1" /> Previous</Button>
                <Button type="button" variant="outline" size="sm" disabled={idx >= items.length - 1} onClick={() => setIdx(idx + 1)} data-testid="bulk-next">Next <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button type="button" className="bg-teal-600 hover:bg-teal-700" disabled={!allValid || busy} onClick={save} data-testid="bulk-save" title={allValid ? "" : "Complete title, category, version and effective date for every file"}>{busy ? "Saving…" : `Save ${items.length} polic${items.length === 1 ? "y" : "ies"}`}</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
